// PropertyScreen — buy, force the value up, flip or rent. The property lesson
// pack: renovation is FORCED appreciation (the lever you control), the flip is
// market-timed (same market weather as trading), and the rent choice turns a
// property from a weekly money DRAIN (upkeep) into a weekly money MACHINE.
// Chrome is hype; the lesson lines stay clear (two-register rule).

import {
  generateListings,
  marketCondition,
  marketMood,
  propertyValue,
  renovationCost,
  saleValue,
  upkeepCost,
  rentIncome,
  MAX_PROPERTIES,
  MAX_RENOVATIONS,
  type PropertyListing,
  type PropertyHolding,
} from '@biz-kid/engine';
import { useDispatch, useGameState, selectCash, selectProperty } from '@biz-kid/store';
import { Card, Eyebrow, Button, Chip, money } from '../../ui/kit.js';

export function PropertyScreen({ onClose }: { onClose: () => void }) {
  const dispatch = useDispatch();
  const property = useGameState(selectProperty);
  const cash = useGameState(selectCash);
  const week = useGameState((s) => s.clock.week);
  const seed = useGameState((s) => s.rngSeed);

  const listings = generateListings(seed, week);
  const boughtThisWeek = property.boughtSlotsWeek === week ? property.boughtSlots : [];
  const condition = marketCondition(seed, week);
  const mood = marketMood(condition);
  const portfolioFull = property.portfolio.length >= MAX_PROPERTIES;

  const moodChip = {
    hot: { tone: 'up' as const, label: '🔥 HOT MARKET — sellers win' },
    steady: { tone: 'neutral' as const, label: '➖ STEADY MARKET' },
    cold: { tone: 'down' as const, label: '🧊 COLD MARKET — buyers win' },
  }[mood];

  return (
    <div className="bk-app" style={{ position: 'fixed', inset: 0, zIndex: 7, overflowY: 'auto' }}>
      <div style={{ maxWidth: 680, margin: '0 auto', padding: 'clamp(14px, 3vw, 26px)', paddingBottom: 90 }}>
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <Eyebrow color="var(--brand)">FLIP IT OR RENT IT</Eyebrow>
            <h1 style={{ margin: '2px 0 0', fontSize: 22, fontWeight: 900, color: 'var(--text)' }}>🏠 PROPERTY PLAY</h1>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Chip tone="brand">{money(cash)}</Chip>
            <Button tone="ghost" size="sm" onClick={onClose}>← HOME</Button>
          </div>
        </header>

        {!property.active ? (
          <Card>
            <p style={{ margin: 0, fontSize: 15, color: 'var(--text)', lineHeight: 1.6 }}>
              Buy a place cheap, renovate it to force the value UP, then flip it when the market is hot —
              or put a tenant in and get paid every single week.
            </p>
            <div style={{ marginTop: 16 }}>
              <Button tone="primary" size="lg" onClick={() => dispatch({ kind: 'StartProperty' })}>OPEN THE BOARD →</Button>
            </div>
          </Card>
        ) : (
          <>
            {/* Portfolio */}
            <Card style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
                <Eyebrow>YOUR PROPERTIES ({property.portfolio.length}/{MAX_PROPERTIES})</Eyebrow>
                <Chip tone={moodChip.tone}>{moodChip.label}</Chip>
              </div>
              {property.portfolio.length === 0 ? (
                <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-2)' }}>
                  Nothing owned yet. Grab an underpriced listing below.
                </p>
              ) : (
                <div style={{ display: 'grid', gap: 10 }}>
                  {property.portfolio.map((p) => (
                    <PropertyRow
                      key={p.id}
                      prop={p}
                      week={week}
                      condition={condition}
                      cash={cash}
                      onRenovate={() => dispatch({ kind: 'PropertyRenovate', propertyId: p.id })}
                      onRent={() => dispatch({ kind: 'PropertyRent', propertyId: p.id })}
                      onSell={() => dispatch({ kind: 'PropertySell', propertyId: p.id })}
                    />
                  ))}
                </div>
              )}
              <p style={{ margin: '12px 0 0', fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.5 }}>
                💡 An empty property costs you upkeep every week. A rented one pays you instead — but a tenant
                means no more renovating. Flip for one big payday, or rent for money that never stops.
              </p>
            </Card>

            {/* Weekly listings */}
            <Card>
              <Eyebrow color="var(--brand)">THIS WEEK'S LISTINGS</Eyebrow>
              <p style={{ margin: '6px 0 12px', fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.5 }}>
                💡 An asking price <b style={{ color: 'var(--text)' }}>below</b> the true value is a deal.
                Each renovation adds value the market has to pay for — that's forced appreciation.
              </p>
              <div style={{ display: 'grid', gap: 10 }}>
                {listings.map((l) => (
                  <ListingRow
                    key={l.slot}
                    listing={l}
                    owned={boughtThisWeek.includes(l.slot)}
                    canAfford={cash >= l.buyPrice}
                    portfolioFull={portfolioFull}
                    onBuy={() => dispatch({ kind: 'PropertyBuy', slot: l.slot })}
                  />
                ))}
              </div>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function PropertyRow({
  prop, week, condition, cash, onRenovate, onRent, onSell,
}: {
  prop: PropertyHolding;
  week: number;
  condition: number;
  cash: number;
  onRenovate: () => void;
  onRent: () => void;
  onSell: () => void;
}) {
  const weeksHeld = week - prop.boughtWeek;
  const tooSoon = weeksHeld < 1;
  const value = propertyValue(prop);
  const sellNow = saleValue(prop, condition);
  const net = sellNow - prop.buyPrice;
  const renoCost = renovationCost(prop);
  const canReno = !prop.rented && prop.renovations < MAX_RENOVATIONS && cash >= renoCost;

  return (
    <div style={{ padding: 12, borderRadius: 14, border: '1px solid var(--border)', background: 'var(--surface-2)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 24, flexShrink: 0 }}>{prop.emoji}</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 800, fontSize: 15, color: 'var(--text)' }}>
            {prop.name}
            {prop.rented && <Chip tone="up">🔑 RENTED</Chip>}
            {prop.renovations > 0 && <Chip tone="brand">{'⭐'.repeat(prop.renovations)}</Chip>}
          </span>
          <span style={{ display: 'block', fontSize: 12, color: 'var(--text-2)' }}>
            Paid {money(prop.buyPrice)} · now worth {money(value)} ·{' '}
            {prop.rented ? (
              <b style={{ color: 'var(--up)' }}>+{money(rentIncome(prop))}/wk rent</b>
            ) : (
              <b style={{ color: 'var(--down)' }}>−{money(upkeepCost(prop))}/wk upkeep</b>
            )}
          </span>
        </span>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
        {!prop.rented && prop.renovations < MAX_RENOVATIONS && (
          <Button tone="soft" size="sm" onClick={onRenovate} disabled={!canReno}>
            🔨 RENOVATE {money(renoCost)}{!canReno && cash < renoCost ? ' 🔒' : ''}
          </Button>
        )}
        {!prop.rented && (
          <Button tone="up" size="sm" onClick={onRent}>
            🔑 RENT IT (+{money(rentIncome(prop))}/wk)
          </Button>
        )}
        {tooSoon ? (
          <span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 700, alignSelf: 'center' }}>Hold ≥1 wk to sell</span>
        ) : (
          <Button tone={net >= 0 ? 'primary' : 'soft'} size="sm" onClick={onSell}>
            💰 SELL {money(sellNow)} ({net >= 0 ? '+' : ''}{money(net)})
          </Button>
        )}
      </div>
    </div>
  );
}

function ListingRow({
  listing, owned, canAfford, portfolioFull, onBuy,
}: {
  listing: PropertyListing;
  owned: boolean;
  canAfford: boolean;
  portfolioFull: boolean;
  onBuy: () => void;
}) {
  const underpriced = listing.buyPrice < listing.fairValue;
  const disabled = owned || portfolioFull || !canAfford;

  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: 12, borderRadius: 14,
        border: underpriced ? '1px solid var(--up)' : '1px solid var(--border)',
        background: owned ? 'var(--surface-3)' : 'var(--surface-2)', opacity: owned ? 0.6 : 1,
      }}
    >
      <span style={{ fontSize: 24, flexShrink: 0 }}>{listing.emoji}</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 800, fontSize: 15, color: 'var(--text)' }}>
          {listing.name}
          {underpriced && !owned && <Chip tone="up">UNDERPRICED</Chip>}
        </span>
        <span style={{ display: 'block', fontSize: 12, color: 'var(--text-2)' }}>
          True value {money(listing.fairValue)}
        </span>
      </span>
      <span style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Asking</div>
        {owned ? (
          <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-3)' }}>✓ BOUGHT</div>
        ) : (
          <Button tone="primary" size="sm" onClick={onBuy} disabled={disabled}>
            {!canAfford ? `${money(listing.buyPrice)} 🔒` : portfolioFull ? 'FULL' : `BUY ${money(listing.buyPrice)}`}
          </Button>
        )}
      </span>
    </div>
  );
}
